<script setup>
import DpcVirtualPhotoBoothFileDialog from './dpc-virtual-photo-booth-file-dialog.vue';
import DpcVirtualPhotoBoothSticker from './dpc-virtual-photo-booth-sticker.vue';
import DpcVirtualPhotoBoothStickerFallback from './dpc-virtual-photo-booth-sticker-fallback.vue';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { estimate, getHandpose, gestureEstimator } from './utils/tensor';
import { refDebounced, useElementSize } from '@vueuse/core';
import { assetPath } from './utils/path';

/*
HEADS UP!

when adding a new preset:

make sure to add the assets to
- `/frontend/src/assets/virtual-photo-booth`
- `/public/virtual-photo-booth`

if svgs are used, make sure a viewbox AND width & height is set in the svg file
else, the image will not be displayed correctly in Firefox
*/
const CONFIG_PRESETS = {
  jasozial: {
    outputName: 'jasozial',
    minWait: 600,
    startPicture: `${assetPath}virtual-photo-booth/jasozial_start.png`,
    sticker: {
      default: {
        src: `${assetPath}virtual-photo-booth/jasozial_thumb.svg`,
        targetSize: 0.6,
        position: {
          x: 0.5,
          y: 0.04,
        },
      },
      hand: {
        src: `${assetPath}virtual-photo-booth/jasozial.svg`,
        targetSize: 0.6,
        position: {
          x: 0.67,
          y: 0.6,
        },
      },
    },
    features: {
      thumbRecognition: true,
    },
  },
  'alles-geht-zusammen': {
    outputName: 'alles-geht-zusammen',
    minWait: 200,
    startPicture: `${assetPath}virtual-photo-booth/alles-geht-zusammen_start.png`,
    sticker: {
      default: {
        src: `${assetPath}virtual-photo-booth/alles-geht-zusammen.svg`,
        targetSize: 0.6,
        position: {
          x: 0.5,
          y: 0.04,
        },
      },
    },
  },
};

const CONFIG_KEY =
  document.querySelector('#virtual-photo-booth')?.dataset?.configPreset ||
  Object.keys(CONFIG_PRESETS)[0];

const CONFIG = CONFIG_PRESETS[CONFIG_KEY];

const until = time =>
  new Promise(resolve => {
    setTimeout(resolve, time);
  });

const scaleKonvaConfig = config => ({
  ...config,
  x: config.x * scaleFactor.value,
  y: config.y * scaleFactor.value,
  width: config.width * scaleFactor.value,
  height: config.height * scaleFactor.value,
});

const STATES = {
  start: 'start',
  loading: 'loading',
  processing: 'processing',
  ready: 'ready',
};

const INFO_TEXTS = {
  empty: '',
  handNotFound: `<strong>Tipp:</strong> Halte einen „Daumen hoch“ gut sichtbar in der Mitte des Bildes – dann wird automatisch das Logo platziert.`,
};

const LOADING_TEXTS = {
  processing: 'Bild wird verarbeitet',
  initialLoad: 'Daten werden geladen',
};

const infoText = ref(INFO_TEXTS.empty);
const loadingText = ref(LOADING_TEXTS.initialLoad);
const state = ref(STATES.start);
const debouncedState = refDebounced(state, 1000);

const stickerElement = ref();
const stageElement = ref();
const { width: stageWidth, height: stageHeight } = useElementSize(stageElement);

const originalInputImage = ref();
const useMirror = ref(false);

const mirrorImage = computed(() => {
  if (!originalInputImage.value) return;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  canvas.width = originalInputImage.value.width;
  canvas.height = originalInputImage.value.height;
  ctx.translate(originalInputImage.value.width, 0);
  ctx.scale(-1, 1);
  ctx.drawImage(originalInputImage.value, 0, 0);
  return canvas;
});

const inputImage = computed(() => {
  if (!originalInputImage.value) return;
  if (!useMirror.value) return originalInputImage.value;
  return mirrorImage.value;
});

const inputBoundingBox = computed(() => ({
  left: 0,
  top: 0,
  right: inputImage.value?.width,
  bottom: inputImage.value?.height,
}));

const isHandRecognized = ref(false);
const handBoundingBox = ref({
  left: 0,
  top: 0,
  right: 0,
  bottom: 0,
});
const resetHand = () => {
  handBoundingBox.value = {
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
  };
  isHandRecognized.value = false;
};

const processImage = async image => {
  originalInputImage.value = image;
};

watch(inputImage, async inputImage => {
  if (!inputImage) return;

  infoText.value = INFO_TEXTS.empty;
  state.value = STATES.loading;
  loadingText.value = LOADING_TEXTS.initialLoad;

  const minWait = until(CONFIG.minWait);

  if (CONFIG.features?.thumbRecognition) {
    const handpose = await getHandpose();

    loadingText.value = LOADING_TEXTS.processing;
    const estimation = await estimate(inputImage, handpose, gestureEstimator);

    await minWait;

    if (!estimation) {
      resetHand();
      state.value = STATES.ready;
      infoText.value = INFO_TEXTS.handNotFound;
      return;
    }

    const { boundingBox } = estimation;

    handBoundingBox.value = boundingBox;
    isHandRecognized.value = true;

    await until(200);

    state.value = STATES.ready;
    return;
  }

  await minWait;
  state.value = STATES.ready;
});

const konvaImage = computed(() => ({
  x: inputBoundingBox.value.left,
  y: inputBoundingBox.value.top,
  width: inputBoundingBox.value.right,
  height: inputBoundingBox.value.bottom,
  image: inputImage.value,
}));

const stickerBoundingBox = computed(
  () =>
    stickerElement.value?.stickerPosition || {
      left: 0,
      top: 0,
      right: 0,
      bottom: 0,
    }
);

const stageConfig = computed(() => {
  if (!inputImage.value) return {};
  if (!stickerBoundingBox.value) return {};

  const PADDING_WHEN_OUT_OF_BOUNDING_BOX_PERCENT = 0.04;
  const paddingWhenOutOfBoundingBox =
    inputImage.value.width * PADDING_WHEN_OUT_OF_BOUNDING_BOX_PERCENT;

  const stickerOutOfInputBoundingBox =
    stickerBoundingBox.value.left < inputBoundingBox.value.left ||
    stickerBoundingBox.value.top < inputBoundingBox.value.top ||
    stickerBoundingBox.value.right > inputBoundingBox.value.right ||
    stickerBoundingBox.value.bottom > inputBoundingBox.value.bottom;

  const shiftY = Math.abs(Math.min(stickerBoundingBox.value.top, 0));
  const shiftX = Math.abs(Math.min(stickerBoundingBox.value.left, 0));
  const width = Math.max(
    inputBoundingBox.value.right + shiftX,
    stickerBoundingBox.value.right
  );
  const height = Math.max(
    inputBoundingBox.value.bottom + shiftY,
    stickerBoundingBox.value.bottom
  );

  return {
    x: stickerOutOfInputBoundingBox ? shiftX + paddingWhenOutOfBoundingBox : 0,
    y: stickerOutOfInputBoundingBox ? shiftY + paddingWhenOutOfBoundingBox : 0,
    width: stickerOutOfInputBoundingBox
      ? width + 2 * paddingWhenOutOfBoundingBox
      : width,
    height: stickerOutOfInputBoundingBox
      ? height + 2 * paddingWhenOutOfBoundingBox
      : height,
  };
});

const scaleFactor = computed(() => {
  if (!inputImage.value) return 1;
  const width = stageConfig.value.width || 1;
  const height = stageConfig.value.height || 1;
  const maxWidth = stageWidth.value;
  const maxHeight = stageHeight.value;
  const widthRatio = maxWidth / width;
  const heightRatio = maxHeight / height;
  const minRatio = Math.min(widthRatio, heightRatio);
  return minRatio;
});

function downloadURI(uri, name) {
  const link = document.createElement('a');
  link.download = name;
  link.href = uri;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
const konvaElement = ref();
const download = () => {
  const dataURL = konvaElement.value
    .getNode()
    .toDataURL(
      { pixelRatio: 1 / scaleFactor.value, mimeType: 'image/jpeg' },
      0.7
    );
  downloadURI(dataURL, `${CONFIG.outputName}.jpg`);
};

const mirror = () => {
  useMirror.value = !useMirror.value;
};

const abort = () => {
  originalInputImage.value = null;
  useMirror.value = false;
  state.value = STATES.start;
  infoText.value = INFO_TEXTS.empty;
  loadingText.value = LOADING_TEXTS.initialLoad;
};

const isHoverOnSticker = ref(false);
const isMouseDownOnSticker = ref(false);

const resetIsMouseDownOnSticker = () => {
  isMouseDownOnSticker.value = false;
};

onMounted(() => {
  window.addEventListener('mouseup', resetIsMouseDownOnSticker);
});
onBeforeUnmount(() => {
  window.removeEventListener('mouseup', resetIsMouseDownOnSticker);
});
</script>

<template>
  <article class="virtual-photo-booth">
    <h3 class="virtual-photo-booth__heading">
      Erstellen Sie hier Ihr eigenes Unterstützerbild! *
    </h3>

    <div
      ref="stageElement"
      class="virtual-photo-booth__stage"
      :class="[
        `virtual-photo-booth__stage--state-${state}`,
        `virtual-photo-booth__stage--debounced-state-${debouncedState}`,
        isHoverOnSticker ? `virtual-photo-booth__stage--sticker-hover` : null,
        isMouseDownOnSticker
          ? `virtual-photo-booth__stage--sticker-mousedown`
          : null,
      ]"
    >
      <template v-if="state === STATES.start">
        <dpc-virtual-photo-booth-file-dialog @image-loaded="processImage">
          Bild auswählen
        </dpc-virtual-photo-booth-file-dialog>
        <img
          class="virtual-photo-booth__background"
          :src="CONFIG.startPicture"
          alt=""
        />
      </template>

      <div
        v-if="[STATES.loading, STATES.ready].includes(state)"
        style="position: relative"
      >
        <v-stage ref="konvaElement" :config="scaleKonvaConfig(stageConfig)">
          <v-layer>
            <v-rect
              :config="
                scaleKonvaConfig({
                  x: stageConfig.x * -1,
                  y: stageConfig.y * -1,
                  width: stageConfig.width,
                  height: stageConfig.height,
                  fill: '#350b59',
                })
              "
            />

            <v-image v-if="inputImage" :config="scaleKonvaConfig(konvaImage)" />

            <template v-if="state === STATES.ready">
              <dpc-virtual-photo-booth-sticker
                v-if="isHandRecognized"
                ref="stickerElement"
                :src="CONFIG.sticker.hand.src"
                :target-size="CONFIG.sticker.hand.targetSize"
                :default-position="CONFIG.sticker.hand.position"
                :hand-bounding-box="handBoundingBox"
                :stage-bounding-box="{
                  left: stageConfig.x * -1,
                  top: stageConfig.y * -1,
                  right: stageConfig.width,
                  bottom: stageConfig.height,
                }"
                :input-bounding-box="inputBoundingBox"
                :scale-factor="scaleFactor"
                @mouseover="isHoverOnSticker = true"
                @mouseout="isHoverOnSticker = false"
                @mousedown="isMouseDownOnSticker = true"
                @mouseup="isMouseDownOnSticker = false"
              />
              <dpc-virtual-photo-booth-sticker-fallback
                v-else
                :src="CONFIG.sticker.default.src"
                :target-size="CONFIG.sticker.default.targetSize"
                :default-position="CONFIG.sticker.default.position"
                :stage-bounding-box="{
                  left: stageConfig.x * -1,
                  top: stageConfig.y * -1,
                  right: stageConfig.width,
                  bottom: stageConfig.height,
                }"
                :input-bounding-box="inputBoundingBox"
                :scale-factor="scaleFactor"
                @mouseover="isHoverOnSticker = true"
                @mouseout="isHoverOnSticker = false"
                @mousedown="isMouseDownOnSticker = true"
                @mouseup="isMouseDownOnSticker = false"
              />
            </template>
          </v-layer>
        </v-stage>

        <button
          v-if="[STATES.ready].includes(state)"
          class="virtual-photo-booth__button-close"
          type="button"
          :disabled="state === STATES.loading"
          @click="abort"
        >
          <span class="icon icon--cross">
            <svg class="icon__svg" role="img" aria-hidden="true">
              <use href="#icon-cross"></use>
            </svg>
            <span class="sr-only">Abbrechen</span>
          </span>
        </button>
      </div>

      <span
        v-if="state === STATES.loading"
        class="virtual-photo-booth__loading"
      >
        <div class="virtual-photo-booth__loading-spinner">
          <svg
            class="virtual-photo-booth__loading-spinner-svg"
            viewBox="25 25 50 50"
            aria-hidden="true"
            role="img"
            focusable="false"
          >
            <circle
              class="virtual-photo-booth__loading-spinner-circle"
              cx="50"
              cy="50"
              r="20"
              fill="none"
              stroke-width="4"
              stroke-miterlimit="10"
            />
          </svg>
        </div>
        <span class="virtual-photo-booth__loading-text">
          {{ loadingText }}
        </span>
      </span>
    </div>

    <div
      class="virtual-photo-booth__infobox virtual-photo-booth__utility-display-none virtual-photo-booth__utility-premobile-display-flex"
      v-if="infoText"
    >
      <span class="virtual-photo-booth__infobox-icon" aria-hidden="true"
        >i</span
      >
      <p class="virtual-photo-booth__infobox-text" v-html="infoText"></p>
    </div>

    <template v-if="[STATES.loading, STATES.ready].includes(state)">
      <div class="virtual-photo-booth__menu">
        <button
          class="virtual-photo-booth__button virtual-photo-booth__button--secondary"
          :disabled="state === STATES.loading"
          type="button"
          @click="mirror"
        >
          <span class="virtual-photo-booth__button-icon icon icon--mirror">
            <svg class="icon__svg" role="img" aria-hidden="true">
              <use href="#icon-mirror"></use>
            </svg>
          </span>

          <span class="virtual-photo-booth__button-text"> Spiegeln </span>
        </button>

        <dpc-virtual-photo-booth-file-dialog
          class="virtual-photo-booth__button--secondary"
          :disabled="state === STATES.loading"
          @image-loaded="processImage"
        >
          <span class="virtual-photo-booth__button-text">
            Neues Bild
            <span
              class="virtual-photo-booth__utility-display-none virtual-photo-booth__utility-premobile-display-inline"
            >
              &nbsp;auswählen
            </span>
          </span>
        </dpc-virtual-photo-booth-file-dialog>
      </div>

      <div class="virtual-photo-booth__action">
        <button
          class="virtual-photo-booth__button virtual-photo-booth__button"
          :disabled="state === STATES.loading"
          type="button"
          @click="download"
        >
          <span class="virtual-photo-booth__button-text">
            Mein Bild downloaden
          </span>
        </button>
      </div>
    </template>

    <div
      class="virtual-photo-booth__infobox virtual-photo-booth__utility-premobile-display-none"
      v-if="infoText"
    >
      <span class="virtual-photo-booth__infobox-icon" aria-hidden="true"
        >i</span
      >
      <p class="virtual-photo-booth__infobox-text" v-html="infoText"></p>
    </div>

    <footer class="virtual-photo-booth__footer">
      <p class="virtual-photo-booth__footnote">
        <strong>* Hinweis zum Datenschutz</strong> <br />
        Die Nutzung der digitalen Fotobox erfolgt freiwillig. Ihr Foto wird
        nicht an einen Server gesendet. Das Bild wird vom Browser generiert.
        Eine Weiterleitung an Dritte erfolgt nicht. Das Bild wird automatisch
        gelöscht, wenn die Website verlassen oder der Vorgang abgebrochen wird.
        Weitere Informationen zum Datenschutz beim VdK finden Sie unter
        <a href="https://www.vdk.de/datenschutz/" target="_blank">
          www.vdk.de/datenschutz
        </a>
      </p>
    </footer>
  </article>
</template>
